import { ChangeDetectionStrategy, Component, computed, inject, OnInit, Renderer2, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { CustomIcon, IconLibrary } from '@finnairoyj/fcom-ui-styles';

import { AspectRatios, FetchPriorityEnum } from '@fcom/ui-components';
import { CountryState } from '@fcom/core';
import { CmsTeaser } from '@fcom/core-api/interfaces';

import { CmsPageComponentMetaData, FinnairThemeService, FragmentService, Links, PageMetaService } from '../../services';
import { getCountryStates } from '../../utils/language.utils';
import { ElementActions, ElementTypes, GaContext } from '../../interfaces';
import { FinnairTheme } from '../../interfaces/theme.interface';
import { CountryAndLanguageSelection } from '../country-and-language-selector/country-and-language-selector.component';
import { GtmService } from '../../gtm';

const parseLocalizedPathFromUrl = (langCode: string, url: string): string => {
  const everythingBeforeLangCode = new RegExp(`.+?(?=/${langCode})`);
  return url ? url.replace(everythingBeforeLangCode, '') : `/${langCode}`;
};

const LANG = 'en';
const DESCRIPTION =
  'Finnair flies between Europe, Asia and North America via Helsinki. Modern fleet - more comfort, lower emissions. Search and book your flights today.';
const META_TAGS: CmsPageComponentMetaData[] = [
  {
    tagName: 'meta',
    attributes: {
      name: 'description',
      content: DESCRIPTION,
    },
  },
  {
    tagName: 'meta',
    attributes: {
      name: 'og:description',
      content: DESCRIPTION,
    },
  },
  {
    tagName: 'meta',
    attributes: {
      name: 'og:site_name',
      content: 'Finnair',
    },
  },
];

@Component({
  selector: 'fin-select-language',
  styleUrls: ['./select-language.component.scss'],
  templateUrl: './select-language.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectLanguageComponent implements OnInit {
  private router = inject(Router);
  private pageMetaService = inject(PageMetaService);
  private renderer = inject(Renderer2);
  private fragmentService = inject(FragmentService);
  private gtmService = inject(GtmService);
  private themeService = inject(FinnairThemeService);

  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly FetchPriorityEnum = FetchPriorityEnum;

  readonly isNewBrand = computed(() => {
    return this.themeService.siteTheme() !== FinnairTheme.LEGACY;
  });

  static readonly TITLE = 'Flight bookings | select country and language | Finnair';

  private readonly countries: CountryState[] = getCountryStates();

  private readonly links = toSignal(this.pageMetaService.links);
  private readonly prevPageUrl = toSignal(this.pageMetaService.prevPageUrl$);

  readonly localizedCountries = computed(() => {
    const links = this.links();
    const prevPageUrl = this.prevPageUrl();
    return this.countries.map((country) => this.mapCountryLanguageLinks(country, links, prevPageUrl));
  });

  private readonly teaser = toSignal(
    this.fragmentService.getFragment<CmsTeaser>('fragments.countryLanguageSelector.url')
  );

  readonly imageUrl = computed(() => {
    return this.teaser()?.picture.responsiveImageLinksData.portrait_ratio1x1['768'];
  });

  readonly imageData = computed(() => {
    return this.teaser()?.picture;
  });

  readonly aspectRatio = signal<keyof typeof AspectRatios>('1x1');

  ngOnInit(): void {
    this.pageMetaService.setTitle(SelectLanguageComponent.TITLE);
    this.pageMetaService.setDocumentLanguage(LANG, this.renderer);
    this.pageMetaService.setMetaTags(META_TAGS, this.renderer);
  }

  applySelection(selection: CountryAndLanguageSelection): void {
    this.gtmService.trackElement(
      'language-selector-select',
      GaContext.LANDING_PAGE,
      ElementTypes.BUTTON,
      selection.languageCode,
      ElementActions.SUBMIT
    );

    const url = this.localizedCountries()
      .find((country) => country.code === selection.country)
      ?.languages.find((lang) => lang.code === selection.languageCode)?.link;
    this.router.navigateByUrl(url || `/${selection.languageCode}`);
  }

  private mapCountryLanguageLinks = (country: CountryState, links: Links, prevPageUrl: string): CountryState => ({
    ...country,
    languages: country.languages.map((language) => ({
      ...language,
      link: this.getLocalizedLinkForLangCode(language.code, links, prevPageUrl),
    })),
  });

  private getLocalizedLinkForLangCode = (langCode: string, links: Links = {}, prevPageUrl: string): string => {
    const localizedCmsPageUrl = parseLocalizedPathFromUrl(langCode, links[langCode]);
    const localizedPrevPageUrl = prevPageUrl ? `/${langCode}${prevPageUrl}` : undefined;
    return localizedPrevPageUrl ?? localizedCmsPageUrl;
  };
}
